import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc,
  getDocs,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from './firebase';
import { addCredits } from './creditService';
import type { CreditPack, PaymentIntent } from '../src/types';

const creditPacksCollection = collection(db, 'credit_packs');
const paymentIntentsCollection = collection(db, 'payment_intents');

// Stripe configuration - using environment variables only
const getStripePublishableKey = (): string => {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    throw new Error('VITE_STRIPE_PUBLISHABLE_KEY environment variable is required');
  }
  return key;
};

// NOTE: Stripe secret key should NEVER be in client-side code
// All secret key operations must be handled server-side in server.js

// Initialize credit packs in database
export const initializeCreditPacks = async (): Promise<void> => {
  try {
    const defaultPacks = [
      {
        name: '5 Credits',
        credits: 5,
        stripeProductId: 'prod_TSinyl4usjAJ5m',
        stripePriceId: 'price_1SVnLQ8ZtYxRZ3sb0Mow5mEI',
        audPriceCents: 500, // $5.00 AUD
        active: true
      },
      {
        name: '25 Credits',
        credits: 25,
        stripeProductId: 'prod_TSinmemKl4fH81',
        stripePriceId: 'price_1SVnLs8ZtYxRZ3sbWV7c4vp2',
        audPriceCents: 1000, // $10.00 AUD
        active: true
      },
      {
        name: '200 Credits',
        credits: 200,
        stripeProductId: 'prod_TSiomU458RpnBx',
        stripePriceId: 'price_1SVnN28ZtYxRZ3sbrSrx9hi5',
        audPriceCents: 4900, // $49.00 AUD
        active: true
      }
    ];

    for (const pack of defaultPacks) {
      // Check if pack already exists
      const q = query(
        creditPacksCollection,
        where('stripePriceId', '==', pack.stripePriceId),
        limit(1)
      );
      const existing = await getDocs(q);
      
      if (existing.empty) {
        await addDoc(creditPacksCollection, {
          ...pack,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        const docRef = existing.docs[0].ref;
        const existingData = existing.docs[0].data();
        const needsUpdate =
          existingData.credits !== pack.credits ||
          existingData.audPriceCents !== pack.audPriceCents ||
          existingData.name !== pack.name ||
          existingData.active !== pack.active ||
          existingData.stripeProductId !== pack.stripeProductId ||
          existingData.stripePriceId !== pack.stripePriceId;
        
        if (needsUpdate) {
          await updateDoc(docRef, {
            ...pack,
            updatedAt: serverTimestamp()
          });
        }
      }
    }
  } catch (error) {
    console.error('Error initializing credit packs:', error);
    throw error;
  }
};

// Get all active credit packs
export const getActiveCreditPacks = async (): Promise<CreditPack[]> => {
  try {
    const q = query(
      creditPacksCollection,
      where('active', '==', true),
      orderBy('credits', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CreditPack));
  } catch (error) {
    console.error('Error getting active credit packs:', error);
    return [];
  }
};

// Get credit pack by price ID
export const getCreditPackByPriceId = async (priceId: string): Promise<CreditPack | null> => {
  try {
    const q = query(
      creditPacksCollection,
      where('stripePriceId', '==', priceId),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    } as CreditPack;
  } catch (error) {
    console.error('Error getting credit pack by price ID:', error);
    return null;
  }
};

// Create checkout session data (for frontend to use)
export const createCheckoutSessionData = async (
  userId: string,
  priceId: string,
  userEmail: string
): Promise<{
  priceId: string;
  quantity: number;
  successUrl: string;
  cancelUrl: string;
  customerEmail: string;
  clientReferenceId: string;
}> => {
  try {
    // Verify the price ID exists
    const pack = await getCreditPackByPriceId(priceId);
    if (!pack) {
      throw new Error('Invalid credit pack selected');
    }
    
    // Create payment intent record for tracking
    const paymentIntentData = {
      stripePaymentIntentId: '', // Will be updated by webhook
      userId,
      packId: pack.id,
      status: 'pending' as const,
      amount: pack.audPriceCents,
      currency: 'aud',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const paymentIntentRef = await addDoc(paymentIntentsCollection, paymentIntentData);
    
    return {
      priceId: pack.stripePriceId,
      quantity: 1,
      successUrl: `${window.location.origin}/account/credits?success=1&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/account/credits?canceled=1`,
      customerEmail: userEmail,
      clientReferenceId: paymentIntentRef.id
    };
  } catch (error) {
    console.error('Error creating checkout session data:', error);
    throw error;
  }
};

// Handle successful payment (called by webhook)
export const handleSuccessfulPayment = async (
  stripePaymentIntentId: string,
  clientReferenceId: string,
  customerEmail: string
): Promise<boolean> => {
  try {
    console.log('Handling successful payment:', { stripePaymentIntentId, clientReferenceId, customerEmail });
    
    // Get payment intent record
    const paymentIntentRef = doc(paymentIntentsCollection, clientReferenceId);
    const paymentIntentSnap = await getDoc(paymentIntentRef);
    
    if (!paymentIntentSnap.exists()) {
      console.error('Payment intent not found:', clientReferenceId);
      return false;
    }
    
    const paymentIntentData = paymentIntentSnap.data() as PaymentIntent;
    
    // Check if already processed (idempotency)
    if (paymentIntentData.status === 'succeeded') {
      console.log('Payment already processed:', stripePaymentIntentId);
      return true;
    }
    
    // Get the credit pack
    const pack = await getCreditPackByPriceId(paymentIntentData.packId);
    if (!pack) {
      console.error('Credit pack not found for payment intent:', clientReferenceId);
      return false;
    }
    
    // Add credits to user's wallet
    await addCredits(
      paymentIntentData.userId,
      pack.credits,
      'stripe_payment',
      stripePaymentIntentId,
      `Purchase: ${pack.name}`
    );
    
    // Update payment intent status
    await updateDoc(paymentIntentRef, {
      stripePaymentIntentId,
      status: 'succeeded',
      updatedAt: serverTimestamp()
    });
    
    console.log(`Successfully credited ${pack.credits} credits to user ${paymentIntentData.userId}`);
    return true;
  } catch (error) {
    console.error('Error handling successful payment:', error);
    return false;
  }
};

// Handle failed payment (called by webhook)
export const handleFailedPayment = async (
  stripePaymentIntentId: string,
  clientReferenceId: string
): Promise<boolean> => {
  try {
    console.log('Handling failed payment:', { stripePaymentIntentId, clientReferenceId });
    
    // Update payment intent status
    const paymentIntentRef = doc(paymentIntentsCollection, clientReferenceId);
    await updateDoc(paymentIntentRef, {
      stripePaymentIntentId,
      status: 'failed',
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error handling failed payment:', error);
    return false;
  }
};

// Get payment history for user
export const getPaymentHistory = async (userId: string): Promise<PaymentIntent[]> => {
  try {
    const q = query(
      paymentIntentsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PaymentIntent));
  } catch (error) {
    console.error('Error getting payment history:', error);
    return [];
  }
};

// Admin functions for pack management
export const createCreditPack = async (packData: Omit<CreditPack, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(creditPacksCollection, {
      ...packData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating credit pack:', error);
    throw error;
  }
};

export const updateCreditPack = async (packId: string, updates: Partial<CreditPack>): Promise<void> => {
  try {
    const packRef = doc(creditPacksCollection, packId);
    await updateDoc(packRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating credit pack:', error);
    throw error;
  }
};

export const getAllCreditPacks = async (): Promise<CreditPack[]> => {
  try {
    const q = query(creditPacksCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CreditPack));
  } catch (error) {
    console.error('Error getting all credit packs:', error);
    return [];
  }
};

// Helper to format currency
export const formatAUDPrice = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)} AUD`;
};

// Initialize Firebase Functions
const functions = getFunctions();

/**
 * Creates a Stripe checkout session for credit purchase using Firebase callable function
 */
export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  userEmail: string,
  successUrl?: string,
  cancelUrl?: string
): Promise<{ sessionId: string; url: string }> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found. Please log in.');
    }

    console.log('Creating checkout session with Firebase callable function');

    // Use Firebase callable function instead of HTTP endpoint
    const createStripeCheckout = httpsCallable(functions, 'createStripeCheckout');
    
    const result = await createStripeCheckout({
      priceId,
      userId,
      userEmail,
      successUrl: successUrl || `${window.location.origin}/account/credits?success=1&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: cancelUrl || `${window.location.origin}/account/credits?canceled=1`
    });

    const data = result.data as { sessionId: string; url: string };
    return data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Gets the Stripe public key using Firebase callable function
 */
export const getStripePublicKey = async (): Promise<string> => {
  try {
    console.log('Getting Stripe public key from Firebase callable function');
    
    // Use Firebase callable function instead of HTTP endpoint
    const getStripePublicKeyFunc = httpsCallable(functions, 'getStripePublicKey');
    const result = await getStripePublicKeyFunc();
    
    const data = result.data as { publicKey: string };
    return data.publicKey || getStripePublishableKey();
  } catch (error) {
    console.warn('Error getting Stripe public key from server, using environment variable:', error);
    return getStripePublishableKey();
  }
};
